import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext, Product, ModelType } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import { useIrisControl } from "../../hooks/useIrisControl";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const EmptyAcceptedView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const { openIris } = useIrisControl();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [hasAddedItem, setHasAddedItem] = useState(false);
  
  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;

  // Get the product - prioritize from navigation state, then fall back to lastAddedProduct
  const detectedProduct = location.state?.detectedProduct;
  const lastAddedProduct = detectedProduct || state.lastAddedProduct;

  // Check if we're in multi-item flow
  const isMultiItemFlow = location.state?.isMultiItemFlow || false;
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 EmptyAcceptedView - Product info:');
    console.log('- Detected product from navigation:', detectedProduct);
    console.log('- Last added product from state:', state.lastAddedProduct);
    console.log('- Using product:', lastAddedProduct);
    console.log('- detectedWeight:', lastAddedProduct?.detectedWeight);
    console.log('- weight:', lastAddedProduct?.weight);
    console.log('- Should show weight display?', lastAddedProduct?.detectedWeight !== undefined && lastAddedProduct?.detectedWeight !== null);

    console.log('🔍 EmptyAcceptedView - Multi-item flow state:');
    console.log('- isMultiItemFlow:', location.state?.isMultiItemFlow);
    console.log('- totalItems:', location.state?.totalItems);
    console.log('- currentItemIndex:', location.state?.currentItemIndex);
    console.log('- remainingItems:', location.state?.remainingItems);
    console.log('- remainingItems.length:', location.state?.remainingItems?.length);
  }, [detectedProduct, state.lastAddedProduct, lastAddedProduct, location.state]);
  
  // Check if the current product is non-branded
  const isNonBrandedProduct = lastAddedProduct && !lastAddedProduct.isBranded;
  
  // Check if continue should be enabled (branded product OR non-branded product that's been accepted)
  const canContinue = !isNonBrandedProduct || state.hasAcceptedUnidentifiedEmpty;

  // Load products from JSON file based on selected model
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // All TREY models use chanel-demo-products.json, otherwise mock-products.json
        const productsFile =
          state.settings.selectedModel === ModelType.TREY_V3_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V4_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V41_SCAN ||
          state.settings.selectedModel === ModelType.TREY_V42_SCAN
            ? '/chanel-demo-products.json'
            : '/mock-products.json';
        
        const response = await fetch(productsFile);
        const productsData = await response.json();
        setProducts(productsData);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };

    loadProducts();
  }, [state.settings.selectedModel]);

  // Don't randomly select a product if we already have one from detection
  useEffect(() => {
    if (products.length > 0 && !hasAddedItem && !isBackFromEmail && !lastAddedProduct) {
      const randomIndex = Math.floor(Math.random() * products.length);
      const selectedProduct = products[randomIndex];
      
      // Preview the selected product without adding to empties yet
      dispatch({ type: 'PREVIEW_PRODUCT', payload: selectedProduct });
      setHasAddedItem(true);
    }
  }, [products, hasAddedItem, dispatch, isBackFromEmail, lastAddedProduct]);

  const handleCancel = () => {
    // In multi-item flow, skip to next item
    const locationState = location.state as any;
    if (locationState?.isMultiItemFlow && locationState?.remainingItems?.length > 0) {
      const nextProduct = locationState.remainingItems[0];
      const newRemainingItems = locationState.remainingItems.slice(1);
      const newIndex = (locationState.currentItemIndex || 0) + 1;

      console.log(`⏭️ Skipping item, processing next (${newIndex + 1}/${locationState.totalItems}):`, nextProduct);

      dispatch({ type: 'PREVIEW_PRODUCT', payload: nextProduct });

      navigate('/empty-accepted', {
        state: {
          detectedProduct: nextProduct,
          isMultiItemFlow: true,
          totalItems: locationState.totalItems,
          currentItemIndex: newIndex,
          remainingItems: newRemainingItems
        },
        replace: true
      });
    } else if (locationState?.isMultiItemFlow) {
      // Last item in multi-item flow - skip it and finish the flow
      console.log('⏭️ Skipping last item in multi-item flow, finishing...');

      // Check if we have any items in the cart already
      if (state.itemsCount > 0) {
        // We have items, go to place-empty to finish
        dispatch({ type: "SET_STEP", payload: 5.5 });
        dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
        navigate("/place-empty");
      } else {
        // No items added at all, go back to add-empty
        navigate("/add-empty");
      }
    } else {
      // Single-item flow - go back to add-empty
      navigate("/add-empty");
    }
  };

  const handleContinue = () => {
    // Check if we're in multi-item flow
    const locationState = location.state as any;
    console.log('🔍 handleContinue - Location state:', locationState);
    console.log('🔍 isMultiItemFlow:', locationState?.isMultiItemFlow);
    console.log('🔍 remainingItems:', locationState?.remainingItems);
    console.log('🔍 remainingItems.length:', locationState?.remainingItems?.length);

    if (locationState?.isMultiItemFlow && locationState?.remainingItems?.length > 0) {
      // Add current product to cart
      if (lastAddedProduct) {
        dispatch({ type: "ADD_EMPTY", payload: lastAddedProduct });
      }

      // Open Iris for 3 seconds to allow item return
      console.log('🔓 Opening Iris for item return (multi-item flow)');
      openIris(3);

      // Process next item
      const nextProduct = locationState.remainingItems[0];
      const newRemainingItems = locationState.remainingItems.slice(1);
      const newIndex = (locationState.currentItemIndex || 0) + 1;

      console.log(`📦 Processing next item (${newIndex + 1}/${locationState.totalItems}):`, nextProduct);

      dispatch({ type: 'PREVIEW_PRODUCT', payload: nextProduct });

      navigate('/empty-accepted', {
        state: {
          detectedProduct: nextProduct,
          isMultiItemFlow: true,
          totalItems: locationState.totalItems,
          currentItemIndex: newIndex,
          remainingItems: newRemainingItems
        },
        replace: true
      });
    } else {
      // Last item in multi-item flow OR single-item flow
      console.log('✅ All items processed or single-item flow');

      // For multi-item flow: add the last item here before navigating
      if (locationState?.isMultiItemFlow && lastAddedProduct) {
        console.log('📦 Adding last item in multi-item flow:', lastAddedProduct.name);
        dispatch({ type: "ADD_EMPTY", payload: lastAddedProduct });

        // Open Iris for the last item
        console.log('🔓 Opening Iris for last item return');
        openIris(3);

        // Navigate to place-empty without fromEmptyAccepted flag (item already added)
        dispatch({ type: "SET_STEP", payload: 5.5 });
        dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
        navigate("/place-empty");
      } else {
        // Single-item flow: let place-empty handle adding and iris
        dispatch({ type: "SET_STEP", payload: 5.5 });

        // Store the product to add after iris opens
        if (lastAddedProduct) {
          dispatch({ type: 'PREVIEW_PRODUCT', payload: lastAddedProduct });
        }

        navigate("/place-empty", {
          state: {
            fromEmptyAccepted: true
          }
        });
      }
    }
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="74:974"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {isNonBrandedProduct ? (
            /* Non-branded product layout */
            <>
              {/* Rows 1-2 - Main heading vertically centered */}
              <div className="row-span-2 flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={getCopy('emptyAccepted.identifiedHeading')} />
                  </motion.div>
                </div>
              </div>

              {/* Row 3 - Message vertically centered with max-width 680px */}
              <div className="flex items-end justify-center">
                <motion.div
                  className="text-center w-full max-w-[680px] font-brand-secondary font-light leading-none not-italic text-[32px] text-brand-text-primary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                 <MultiLineCopy 
                  text={getCopy('emptyAccepted.nonBrandedText')}
                  lineClassName="font-normal"
                  className="leading-[42px]"
                />
                </motion.div>
              </div>
            </>
            
          ) : (
            /* Branded product layout */
            <>
              {/* Row 1 - Main heading */}
              <div className="flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={isMultiItemFlow ? "Return this item?" : getCopy('emptyAccepted.heading')} />
                  </motion.div>
                </div>
              </div>

              {/* Rows 2 & 3 - Product image and details spanning two rows */}
              <div className="row-span-2 flex flex-col items-center justify-center">
                {/* Product image - taking all available space minus text height */}
                <motion.div
                  className="w-full flex-1 bg-center bg-cover bg-no-repeat"
                  style={{
                    backgroundImage: lastAddedProduct
                      ? `url('${lastAddedProduct.image}')`
                      : "url('/brand-ultra-facial-cream.png')",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                />
                
                {/* Product details - natural flow at bottom */}
                <motion.div
                  className="text-center w-full max-w-[920px] flex-shrink-0"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <div className="flex flex-col gap-4 items-center py-4">
                    <h3 className="font-brand-secondary font-normal text-[32px] leading-[42px] text-brand-text-primary text-center">
                      {lastAddedProduct ? lastAddedProduct.name : getCopy('productFallback.defaultProductName')}
                    </h3>
                    <div className="flex items-center justify-center">
                      <span className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted text-center">
                        {lastAddedProduct ? lastAddedProduct.size : getCopy('productFallback.defaultProductSize')}
                      </span>
                    </div>

                    {/* Weight Information Display (Detected vs Expected) */}
                    {lastAddedProduct && typeof lastAddedProduct.detectedWeight === 'number' && (
                      <motion.div
                        className="text-center mt-4 p-4 rounded-lg bg-gray-50"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                      >
                        <div className="flex items-center justify-center gap-6 text-lg font-brand-secondary">
                          <div className="flex flex-col items-center">
                            <span className="text-sm text-gray-500 mb-1">Detected</span>
                            <span className="text-xl font-semibold text-brand-text-primary">
                              {lastAddedProduct.detectedWeight.toFixed(1)}g
                            </span>
                          </div>

                          {typeof lastAddedProduct.weight === 'number' && (
                            <>
                              <div className="w-px h-10 bg-gray-300" />
                              <div className="flex flex-col items-center">
                                <span className="text-sm text-gray-500 mb-1">Expected</span>
                                <span className="text-xl font-semibold text-gray-700">
                                  {lastAddedProduct.weight}g
                                  {lastAddedProduct.weightTolerance && (
                                    <span className="text-sm text-gray-500 ml-1">
                                      ±{lastAddedProduct.weightTolerance}g
                                    </span>
                                  )}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Reward Values - Only show for branded products */}
                    {((lastAddedProduct && lastAddedProduct.isBranded && lastAddedProduct.rewards) || (!lastAddedProduct)) && (
                      <motion.div
                        className="flex flex-col gap-2 items-center mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.6,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <div className="font-brand-secondary font-normal text-2xl mb-2 leading-[23px] text-brand-text-primary text-center">
                          Reward value
                        </div>
                        <div className="flex flex-row items-start justify-start">
                          {/* Left section - Points */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-4 pr-3 py-1 rounded-bl-[100px] rounded-tl-[100px]">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center w-[53px]">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.loyaltyPoints : 0}
                            </div>
                          </div>
                          {/* Middle section - Dollar Value */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center px-3 py-1 relative border-l-2 border-r-2 border-brand-background">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center whitespace-nowrap">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.dollarValue : "$0.00"}
                            </div>
                          </div>
                          {/* Right section - Discount */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-3 pr-4 py-1 rounded-br-[100px] rounded-tr-[100px] w-20">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center flex-1">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.discountValue : "0%"}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Multi-item progress indicator */}
          {(location.state as any)?.isMultiItemFlow && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                padding: '1rem 2rem',
                background: 'rgba(76, 175, 80, 0.1)',
                border: '2px solid rgba(76, 175, 80, 0.3)',
                borderRadius: '12px',
                textAlign: 'center',
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#4CAF50'
              }}
            >
              Processing Item {((location.state as any).currentItemIndex || 0) + 1} of {(location.state as any).totalItems}
            </motion.div>
          )}

          {/* Buttons */}
          <div className="flex flex-row gap-6 lg:gap-10 w-full">
            <Button variant="secondary" onClick={handleCancel}>
              {isMultiItemFlow ? "Skip item" : "Cancel"}
            </Button>
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={!canContinue}
              className={!canContinue ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {isMultiItemFlow ? "Confirm return" : getCopy('emptyAccepted.buttons.primary')}
            </Button>
          </div>



          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default EmptyAcceptedView;
